import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KeyRound, Lock, Trash2, Users as UsersIcon } from "lucide-react";
import { fmtDT } from "@/lib/format";

export const STATUSES = [
  { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "on_leave", label: "On Leave" },
  { value: "suspended", label: "Suspended" }, { value: "terminated", label: "Terminated" },
];

export const statusChip = (s) => ({
  active: "bg-signal-green/15 text-signal-green", inactive: "bg-muted text-muted-foreground", on_leave: "bg-signal-amber/15 text-signal-amber",
  suspended: "bg-signal-red/15 text-signal-red", terminated: "bg-signal-red/25 text-signal-red",
}[s] || "bg-muted text-muted-foreground");

const HEADERS = ["Employee ID", "Name", "Email", "Mobile", "Department", "Designation", "Reports To", "Scope", "Status", "Role", "Last Login", ""];

const headerVisibility = {
  "Employee ID": "hidden sm:table-cell",
  Email: "hidden lg:table-cell",
  Mobile: "hidden xl:table-cell",
  Department: "hidden lg:table-cell",
  Designation: "hidden xl:table-cell",
  "Reports To": "hidden xl:table-cell",
  Scope: "hidden xl:table-cell",
  Status: "hidden sm:table-cell",
  Role: "hidden lg:table-cell",
  "Last Login": "hidden xl:table-cell",
};

export function UserTable({ users, roles, me, onRole, onStatus, onReset, onPin, onReassign, onDelete }) {
  const canManageUsers = me?.role === "admin";
  const headers = canManageUsers
    ? HEADERS
    : ["Name", "Department", "Designation", "Reports To", "Status"];
  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm" data-testid="users-table">
        <thead>
          <tr className="border-b border-border text-left">
            {headers.map((h) => (
              <th
                key={h}
                className={`px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground ${headerVisibility[h] || ""}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow key={u.user_id} u={u} users={users} roles={roles} me={me} canManageUsers={canManageUsers} onRole={onRole} onStatus={onStatus} onReset={onReset} onPin={onPin} onReassign={onReassign} onDelete={onDelete} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({ u, users, roles, me, canManageUsers, onRole, onStatus, onReset, onPin, onReassign, onDelete }) {
  const isMe = u.user_id === me?.user_id;
  const status = u.status || "active";
  if (!canManageUsers) {
    return (
      <tr className="border-b border-border/50" data-testid={`user-row-${u.user_id}`}>
        <td className="px-4 py-3 font-medium">{u.name}</td>
        <td className="px-4 py-3 text-muted-foreground">{u.department || "—"}</td>
        <td className="px-4 py-3 text-muted-foreground text-xs">{u.designation || "—"}</td>
        <td className="px-4 py-3 text-muted-foreground">
          {u.manager_id ? (users.find((m) => m.user_id === u.manager_id)?.name || "—") : "—"}
        </td>
        <td className="px-4 py-3">
          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${statusChip(status)}`}>
            {status}
          </span>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-border/50" data-testid={`user-row-${u.user_id}`}>
      <td className="hidden px-4 py-3 font-mono text-xs text-muted-foreground sm:table-cell">{u.employee_id || "—"}</td>
      <td className="px-3 py-3 font-medium sm:px-4">
        {u.name}
        <span className="block font-mono text-[10px] text-muted-foreground sm:hidden">
          {u.employee_id || "—"} · {u.role || "—"}
        </span>
        <span className={`mt-1 inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded sm:hidden ${statusChip(status)}`}>
          {status}
        </span>
        {u.pin_lockout && (
          <span className="ml-2 text-[10px] font-mono uppercase text-signal-red" data-testid={`pin-lock-${u.user_id}`}>
            PIN locked
          </span>
        )}
      </td>
      <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">{u.email}</td>
      <td className="hidden px-4 py-3 text-xs text-muted-foreground xl:table-cell">{u.mobile || "—"}</td>
      <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">{u.department || "—"}</td>
      <td className="hidden px-4 py-3 text-xs text-muted-foreground xl:table-cell">{u.designation || "—"}</td>
      <td className="hidden px-4 py-3 text-muted-foreground xl:table-cell">{u.manager_id ? (users.find((m) => m.user_id === u.manager_id)?.name || "—") : "—"}</td>
      <td className="hidden px-4 py-3 xl:table-cell"><span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-muted text-muted-foreground" data-testid={`scope-${u.user_id}`}>{u.data_scope || (u.role === "admin" ? "ALL" : roles.find((r) => r.role_id === u.role)?.data_scope || "—")}</span></td>
      <td className="hidden px-4 py-3 sm:table-cell">
        {isMe ? (
          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${statusChip(status)}`}>{status}</span>
        ) : (
          <Select value={status} onValueChange={(v) => onStatus(u.user_id, v)}>
            <SelectTrigger className={`w-32 h-8 text-[10px] font-mono uppercase tracking-widest ${statusChip(status)}`} data-testid={`status-select-${u.user_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`status-${s.value}-${u.user_id}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="hidden px-4 py-3 lg:table-cell">
        <Select value={u.role} onValueChange={(v) => onRole(u.user_id, v)}>
          <SelectTrigger className="w-40 h-8" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name} ({r.department})</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="hidden px-4 py-3 text-xs text-muted-foreground whitespace-nowrap xl:table-cell">{u.last_login ? fmtDT(u.last_login) : "Never"}</td>
      <td className="px-2 py-3 sm:px-4">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            title={`Reset password for ${u.name}`}
            aria-label={`Reset password for ${u.name}`}
            onClick={() => onReset(u)}
            data-testid={`reset-password-${u.user_id}`}
          >
            <KeyRound className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">PW</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            title={`Set PIN for ${u.name}`}
            aria-label={`Set PIN for ${u.name}`}
            onClick={() => onPin(u)}
            data-testid={`set-pin-${u.user_id}`}
          >
            <Lock className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">PIN</span>
          </Button>
          {!isMe && (
            <Button size="sm" variant="ghost" title="Reassign open tasks" className="hidden sm:inline-flex" onClick={() => onReassign(u)} data-testid={`reassign-${u.user_id}`}><UsersIcon className="w-3.5 h-3.5" /></Button>)}
          {!isMe && (
            <Button size="sm" variant="ghost" title="Terminate login access" className="hidden sm:inline-flex" onClick={() => onDelete(u)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="w-3.5 h-3.5 text-signal-red" /></Button>)}
        </div>
      </td>
    </tr>
  );
}

export function LoginAuditTable({ rows, state }) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid="login-audit-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            {["Login Time", "User", "Employee ID", "Method", "Result", "Reason", "IP", "Logout"].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.audit_id} className="border-b border-border/50" data-testid={`audit-${a.audit_id}`}>
              <td className="px-4 py-2.5 text-xs metric whitespace-nowrap">{fmtDT(a.login_at)}</td>
              <td className="px-4 py-2.5">{a.name || a.email || "—"}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{a.employee_id || "—"}</td>
              <td className="px-4 py-2.5 text-xs uppercase">{a.method}</td>
              <td className="px-4 py-2.5">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${a.success ? "bg-signal-green/15 text-signal-green" : "bg-signal-red/15 text-signal-red"}`}>{a.success ? "Success" : "Failed"}</span>
              </td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{(a.reason || "").replace(/_/g, " ") || "—"}</td>
              <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{a.ip || "—"}</td>
              <td className="px-4 py-2.5 text-xs metric text-muted-foreground whitespace-nowrap">{a.logout_at ? fmtDT(a.logout_at) : "—"}</td>
            </tr>))}
          {state === "loading" && <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-muted-foreground" data-testid="audit-loading">Loading login activity…</td></tr>}
          {state === "error" && <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-signal-red" data-testid="audit-error">Could not load login activity.</td></tr>}
          {state === "ok" && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-muted-foreground">No login activity recorded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
